import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ShieldCheck, Lock } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { PackCheckLogo } from '../components/PackCheckLogo';

export default function AdminLogin() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true); setError('');
    try {
      const { data } = await api.post('/auth/admin-password', { password });
      if (data.session_token) localStorage.setItem('pc_session_token', data.session_token);
      await refresh();
      nav('/admin', { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Invalid password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center p-6">
      <form onSubmit={submit} className="w-full max-w-md panel p-10 space-y-6" data-testid="admin-login-form">
        <div className="flex flex-col items-start gap-2">
          <Link to="/" aria-label="Home">
            <PackCheckLogo height={32} />
          </Link>
          <div className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-accent border-gradient-pt px-2 py-1" data-testid="super-admin-tag">
            <ShieldCheck size={11} strokeWidth={1.8} /> Super-admin
          </div>
        </div>

        <div>
          <div className="label-mono mb-2">Restricted access</div>
          <h1 className="text-2xl font-medium tracking-tight">Sign in with admin password</h1>
          <p className="mt-2 text-sm text-muted-foreground">No email required. Password-only access for the PackCheck super-admin.</p>
        </div>

        <div>
          <Label className="label-mono-sm">Password</Label>
          <div className="mt-1 relative">
            <Lock size={14} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              className="rounded-none pl-9 h-11"
              autoFocus
              data-testid="admin-password-input"
            />
          </div>
        </div>

        {error && (
          <div className="text-sm text-severity-high font-mono" data-testid="admin-login-error">{error}</div>
        )}

        <Button type="submit" disabled={loading || !password} className="w-full h-11 rounded-none" data-testid="admin-login-submit">
          {loading ? 'Verifying…' : 'Sign in'}
        </Button>

        <div className="pt-2 border-t border-border text-xs text-muted-foreground">
          Not an admin? <Link to="/" className="underline">Return home</Link> and use Google sign-in instead.
        </div>
      </form>
    </div>
  );
}
